import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { OrganizationDatabaseLogsResultStore, CheckDatabaseLogResultItem } from 'src/stores/organizations-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Button, Card, Modal, Row } from 'antd';
import { Query } from 'src/core/stores/data-store';
import ExportDBLogsModal from './organization-db-logs-export-modal';
import { OrganizationLogsContext } from '..';
import { emptyTableModel } from 'src/common/utils';
import { formatMessage } from 'src/core/services/http.service';
import { ENV } from 'src/';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';

const OrganizationDbLogsResult: FC = () => {
  const config: AppConfiguration = useContext(AppConfig);
  const params = useParams();
  const logId = params.id!;
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [query, setQuery] = useState({
    searchQuery: '',
    skip: 0,
    take: 10,
  } as Query);

  const logResultStore = container.get(OrganizationDatabaseLogsResultStore);
  const logResultState = logResultStore.state;

  const [exportModalVisible, setExportModalVisible] = useState(false);

  const { setActiveTab } = useContext(OrganizationLogsContext);

  if (location.state) setHeader(t('DatabaseCheckResultOnDate').replace('{0}', location.state.date));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'organizations', breadcrumbName: t('Organization') },
    { path: 'organizations/logs', breadcrumbName: t('OrganizationLogs') },
    { path: `organizations/logs/db-logs/${logId}`, breadcrumbName: t('DatabaseCheckLogsResult') },
  ]);

  useEffect(
    () => {
      if (!location.state) {
        navigate('/organizations');
        return;
      }
      logResultStore.loadById(logId, query);
      setActiveTab('database');
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'organizationName',
        title: t('OrganizationName'),
        width: '25%',
        renderer: (data) => (
          <span
            className="clickable-span"
            onClick={() => {
              Modal.destroyAll();
              navigate(`/organizations/${data.organizationId}`, { state: location.state });
            }}
          >
            {data.organizationName}
          </span>
        ),
      },
      {
        sortable: true,
        searchable: true,
        sortField: 'result',
        useProfile: true,
        field: 'errorMessage',
        title: t('Result'),
        renderer: (data) => (
          <span style={{ color: data.success ? '#53c273' : 'red' }}>{data.success ? t('Success') : `Error: ${data.errorMessage}`}</span>
        ),
      },
    ],
    data: { ...logResultState.value, isBusy: logResultState.value.isBusy },
  } as TableModel<CheckDatabaseLogResultItem>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            logResultStore.loadById(logId, query);
          }}
          onRefresh={() => logResultStore.loadById(logId, query)}
          model={logResultState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            logResultState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(logResultState.errorMessage.value) : t('DatabaseLogsTableError'))
          }
          rightToolbar={
            <li>
              <Button
                type="primary"
                onClick={() => {
                  console.log('sdf');
                  setExportModalVisible(true);
                }}
              >
                {t('Export')}
              </Button>
            </li>
          }
        />
        {exportModalVisible && (
          <ExportDBLogsModal
            t={t}
            logId={logId}
            onClose={() => setExportModalVisible(false)}
            onExport={() => {
              logResultStore.loadById(logId, query);
              setExportModalVisible(false);
            }}
          />
        )}
      </Card>
    </Row>
  );
};

export default OrganizationDbLogsResult;
